import { useCallback, useEffect, useRef, useState } from "react";

import { apiListProfitPushes } from "api/purchasing/profitExport";
import type { ProfitPushStatus } from "api/types";

import { isOpenProfitState } from "../types/profitPushState";

/** Cada cuánto se relee el estado mientras haya alguna orden sin resolver. */
const POLL_INTERVAL_MS = 15000;

type State = {
  byOrderId: Record<string, ProfitPushStatus>;
  /** Primera carga sin terminar: hasta acá no se sabe qué está bloqueado. */
  isLoading: boolean;
  /** La consulta de apertura falló. Mientras esté puesto, todo se bloquea. */
  loadError: string;
};

const INITIAL: State = { byOrderId: {}, isLoading: false, loadError: "" };

/**
 * Estado de las órdenes de una simulación frente a Profit.
 *
 * Dos decisiones que no son evidentes leyendo el código:
 *
 * **Si la consulta de apertura falla, se bloquea todo** en vez de abrir
 * editable. Un drawer editable sin poder verificar qué se envió es la vía
 * directa a mandar dos veces la misma orden, y no hay forma de anular la
 * segunda desde la aplicación.
 *
 * **El polling sólo corre mientras haya algo abierto** (`sending`/`pending`) y
 * se corta al cerrar el drawer. Un intervalo que sigue vivo con todo resuelto
 * es tráfico contra el datamart que no cambia nada.
 */
export function useProfitPushStatus(
  simulationId: string | null,
  enabled: boolean,
) {
  const [state, setState] = useState<State>(INITIAL);
  // El polling no debe re-crear su intervalo en cada render sólo porque cambió
  // el mapa de estados; lee la bandera desde el ref.
  const hasOpenRef = useRef(false);

  const load = useCallback(
    async (options?: { refresh?: boolean; silent?: boolean }) => {
      if (!simulationId) {
        setState(INITIAL);
        return;
      }
      if (!options?.silent) {
        setState((prev) => ({ ...prev, isLoading: true }));
      }
      try {
        const rows = await apiListProfitPushes(simulationId, {
          refresh: options?.refresh,
        });
        const byOrderId: Record<string, ProfitPushStatus> = {};
        rows.forEach((row) => {
          if (row.order_id) {
            byOrderId[row.order_id] = row;
          }
        });
        hasOpenRef.current = rows.some((row) => isOpenProfitState(row.state));
        setState({ byOrderId, isLoading: false, loadError: "" });
      } catch (err) {
        hasOpenRef.current = false;
        setState((prev) => ({
          // Se conserva lo último conocido: sirve para mostrar los badges
          // viejos, pero el `loadError` es lo que decide el bloqueo.
          byOrderId: prev.byOrderId,
          isLoading: false,
          loadError:
            "No se pudo verificar el estado de las órdenes en Profit. " +
            "Se bloqueó la edición para evitar enviar una orden dos veces.",
        }));
      }
    },
    [simulationId],
  );

  // Apertura: se consulta el datamart, no el registro local.
  useEffect(() => {
    if (!enabled || !simulationId) {
      return;
    }
    void load({ refresh: true });
  }, [enabled, simulationId, load]);

  useEffect(() => {
    if (!enabled || !simulationId) {
      return;
    }
    const timer = window.setInterval(() => {
      if (!hasOpenRef.current) {
        return;
      }
      void load({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, simulationId, load]);

  return {
    ...state,
    /** Vuelve a consultar el datamart. Lo usa el reintento del aviso de error. */
    reload: () => load({ refresh: true }),
    /** Refresco local tras enviar, sin pegarle de nuevo al datamart. */
    reloadLocal: () => load({ silent: true }),
    /** Aplica el estado devuelto por un envío recién hecho. */
    apply: (statuses: ProfitPushStatus[]) =>
      setState((prev) => {
        const byOrderId = { ...prev.byOrderId };
        statuses.forEach((row) => {
          if (row.order_id) {
            byOrderId[row.order_id] = row;
          }
        });
        hasOpenRef.current = Object.values(byOrderId).some((row) =>
          isOpenProfitState(row.state),
        );
        return { ...prev, byOrderId, loadError: "" };
      }),
  };
}
